import { useEffect, useRef, useState } from 'react';
import { bt } from '../../i18n';
import type { MenuField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { listMenus, type MenuSummary } from '../../lib/menusApi';

/**
 * Menu picker (Shopify `link_list`) — the merchant picks WHICH of the store's named menus a footer/header
 * block displays (menus are authored once in dashboard → Navigation). A custom in-app listbox (never a
 * native `<select>`, like SelectControl), label left / control right. Options are fetched lazily on first
 * open (the Topbar product-picker recipe) so opening the inspector costs nothing. The stored value is the
 * menu HANDLE; "None" stores `''`. Commits immediately (one clean undo entry).
 */
export function MenuControl({
  field,
  value,
  onChange,
}: {
  field: MenuField;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span" layout="row">
      <MenuListbox current={value ?? ''} label={field.label} onChange={onChange} />
    </FieldRow>
  );
}

function MenuListbox({
  current,
  label,
  onChange,
}: {
  current: string;
  label: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menus, setMenus] = useState<MenuSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Lazy fetch on first open (retries on re-open if it hasn't succeeded), with an `alive` guard.
  // IMPORTANT: `loading` must NOT be in the guard or the deps — including it makes `setLoading(true)`
  // re-run this effect, whose cleanup flips the in-flight run's `alive` to false, so the settled fetch
  // never clears `loading` and the picker sticks on "Loading menus…" forever (success OR failure).
  useEffect(() => {
    if (!open || menus !== null) return;
    let alive = true;
    setLoading(true);
    setError(false);
    // Pair each state update with setLoading(false) in the SAME callback so React batches them into one
    // render. If setLoading(false) were split into `.finally`, the setMenus render would re-run this effect
    // (menus is a dep) and its cleanup would flip `alive` to false before `.finally` ran — leaving loading
    // stuck on. Keeping them together applies both while `alive` is still true.
    listMenus()
      .then((m) => {
        if (alive) {
          setMenus(m);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [open, menus]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const currentLabel = !current
    ? bt('chrome.common.none', 'None')
    : (menus?.find((m) => m.handle === current)?.title ?? current);

  const choose = (handle: string) => {
    onChange(handle);
    setOpen(false);
  };

  return (
    <div className="bld-listbox" ref={ref}>
      <button
        type="button"
        className="bld-listbox__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="bld-listbox__val">{currentLabel}</span>
        <svg
          className="bld-listbox__caret"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 6.5L8 3.5L11 6.5M5 9.5L8 12.5L11 9.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul className="bld-listbox__menu" role="listbox" aria-label={label}>
          <li
            role="option"
            aria-selected={current === ''}
            className="bld-listbox__opt"
            onClick={() => choose('')}
          >
            None
          </li>
          {loading && (
            <li className="bld-listbox__opt bld-listbox__opt--hint">
              {bt('chrome.menu.loading', 'Loading menus…')}
            </li>
          )}
          {error && (
            <li className="bld-listbox__opt bld-listbox__opt--hint">
              {bt('chrome.menu.failed', 'Couldn’t load menus.')}
            </li>
          )}
          {!loading && !error && menus?.length === 0 && (
            <li className="bld-listbox__opt bld-listbox__opt--hint">
              No menus yet — create one in Navigation.
            </li>
          )}
          {menus?.map((m) => (
            <li
              key={m.handle}
              role="option"
              aria-selected={m.handle === current}
              className="bld-listbox__opt"
              onClick={() => choose(m.handle)}
            >
              {m.title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
